import React, { useContext, useEffect, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";
import { QREditorContext } from "@/contexts/QREditorContext";
import { checkValidHexColor } from "@/utils/formHelper";
import { ColorGroupThemePickerControlledProps, FormState, FormValues } from "./types";
import { useFormContext } from "react-hook-form";
import { InputThemePicker } from "../InputThemePicker";
import "./styles.scss";

/**
 * Componente específico para manejo de themes
 *
 * @note Este componente es una versión modificada de ColorGroupControlled
 *       con lógica específica para themes. La duplicación es necesaria ya que
 *       la lógica original no puede ser reutilizada directamente.
 */
export const ColorGroupThemePickerControlled = (props: ColorGroupThemePickerControlledProps) => {
  const {
    primaryColor: {
      id: primaryColorId,
      label: primaryColorLabel,
      color: primaryColor,
      className: primaryColorClassName,
    },
    secondaryColor: {
      id: secondaryColorId,
      label: secondaryColorLabel,
      color: secondaryColor,
      className: secondaryColorClassName,
    },
    className,
    rules,
  } = props;

  const qrEditorContext = useContext(QREditorContext);
  const { qrData, setQrData } = qrEditorContext ?? {};
  const methods = useFormContext<FormValues>();
  const [buttonColor, setButtonColor] = useState(primaryColor);
  const [backgroundColor, setBackgroundColor] = useState(secondaryColor);

  const { clearErrors, setError, getValues, setValue, formState } = methods;
  const { errors } = formState as FormState;

  const handleSwapColors = () => {
    if (!qrEditorContext) {
      return null;
    }

    const isValidButtonColor = isValidColor(buttonColor, "themeColor.buttonColor");
    const isValidBackgroundColor = isValidColor(backgroundColor, "themeColor.backgroundColor");

    if (isValidButtonColor && isValidBackgroundColor) {
      clearErrors("themeColor.buttonColor");
      clearErrors("themeColor.backgroundColor");

      setButtonColor(backgroundColor);
      setBackgroundColor(buttonColor);

      setValue("themeColor", {
        buttonColor: backgroundColor,
        backgroundColor: buttonColor,
      });
    }
  };

  const isValidColor = (
    color: string,
    fieldName: "themeColor.buttonColor" | "themeColor.backgroundColor"
  ) => {
    const isValid = checkValidHexColor(color);

    if (rules?.required?.value && !color) {
      setError(fieldName, {
        type: "required",
        message: rules.required.message || "Required field",
      });
      return false;
    }

    if (!isValid) {
      setError(fieldName, {
        type: "pattern",
        message: rules.pattern.message || "The color is not valid",
      });
      return false;
    }

    return true;
  };

  const handleButtonColorChange = (e: any) => {
    const isValid = isValidColor(e, "themeColor.buttonColor");
    setButtonColor(e);
    setValue("themeColor", {
      ...getValues("themeColor"),
      buttonColor: e,
    });

    if (isValid) {
      clearErrors("themeColor.buttonColor");
    }
  };

  const handleBackgroundColorChange = (e: any) => {
    const isValid = isValidColor(e, "themeColor.backgroundColor");
    setBackgroundColor(e);
    setValue("themeColor", {
      ...getValues("themeColor"),
      backgroundColor: e,
    });

    if (isValid) {
      clearErrors("themeColor.backgroundColor");
    }
  };

  useEffect(() => {
    if (!qrEditorContext) return;

    const validBtn = checkValidHexColor(buttonColor) || buttonColor === "";
    const validBg = checkValidHexColor(backgroundColor) || backgroundColor === "";

    if (!validBtn || !validBg) return;

    const nextButton = buttonColor === "" ? primaryColor : buttonColor;
    const nextBackground = backgroundColor === "" ? secondaryColor : backgroundColor;

    if (
      qrData?.data?.themeColor?.buttonColor === nextButton &&
      qrData?.data?.themeColor?.backgroundColor === nextBackground
    ) {
      return;
    }

    setQrData({
      ...qrEditorContext.qrData,
      data: {
        ...qrEditorContext.qrData.data,
        themeColor: {
          ...qrEditorContext.qrData.data.themeColor,
          buttonColor: nextButton,
          backgroundColor: nextBackground,
        },
      },
    });
  }, [buttonColor, backgroundColor, primaryColor, secondaryColor, setQrData, qrData]);

  return (
    <div
      suppressHydrationWarning
      data-testid="color-group-theme-picker"
      data-qa="color-group-theme-picker"
      className={`color-group ${className || ""}`}>
      <InputThemePicker
        id={secondaryColorId}
        label={secondaryColorLabel}
        type="text"
        colorPicker={backgroundColor}
        colorPickerWithDebounce={true}
        value={backgroundColor}
        className={secondaryColorClassName}
        placeholder={secondaryColor}
        name={secondaryColorId}
        defaultColorValue={secondaryColor}
        error={errors.themeColor?.backgroundColor}
        errorMessage={errors.themeColor?.backgroundColor?.message}
        onChange={handleBackgroundColorChange}
      />

      <ButtonLib
        shape="ghost"
        id="swap-color-button"
        // FIXME: There's a problem with the type
        // dataTestId="color-group-theme-picker-button"
        dataQA="color-group-theme-picker-button"
        iconLeft={<FiRefreshCw />}
        ariaLabel="Swap colors"
        onClick={handleSwapColors}
      />

      <InputThemePicker
        type="text"
        id={primaryColorId}
        label={primaryColorLabel}
        value={buttonColor}
        colorPicker={buttonColor}
        colorPickerWithDebounce={true}
        className={primaryColorClassName}
        placeholder={primaryColor}
        name={primaryColorId}
        defaultColorValue={primaryColor}
        error={errors.themeColor?.buttonColor}
        errorMessage={errors.themeColor?.buttonColor?.message}
        onChange={handleButtonColorChange}
      />
    </div>
  );
};
